/* eslint-disable max-lines */
import {
  useCustomComments,
  useUpdateAlarm,
} from '@hakimo-ui/hakimo/data-access';
import {
  Alarm,
  DataVersion,
  displayStatusTypes,
  StatusType,
} from '@hakimo-ui/hakimo/types';
import { Select } from '@hakimo-ui/hakimo/ui-elements';
import {
  eventTracker,
  getSelectItemsSelectable,
  toast,
  useCanChangeStatusNoComment,
} from '@hakimo-ui/hakimo/util';
import {
  Alert,
  Button,
  Modal,
  Radio,
  Selectable,
} from '@hakimo-ui/shared/ui-base';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useState } from 'react';
import { displayStatusSelectables } from '../../shared/constants';
import { mapStatusToStatusType } from '../../shared/util/common';
import { checkNeedsComment } from './util';
dayjs.extend(utc);

interface Props {
  open: boolean;
  alarm: Alarm;
  onCloseCb: () => void;
  onResolveCb?: () => void;
  dataVersion?: DataVersion;
}

export function AlarmStatusModal(props: Props) {
  const { open, alarm, onCloseCb, onResolveCb, dataVersion } = props;
  const [comment, setComment] = useState<Selectable | null>(null);
  const [query, setQuery] = useState<string>();
  const currentStatusType = mapStatusToStatusType(alarm.status);
  const { data: commentOptions } = useCustomComments();
  const mutation = useUpdateAlarm(
    alarm.id,
    (_, variables) => {
      if (
        variables?.newStatus ===
          displayStatusTypes.find((item) => item === 'Resolved') &&
        onResolveCb
      ) {
        onResolveCb();
      }
      toast('Alarm status updated', { type: 'success' });
      onCloseCb();
    },
    dataVersion
  );
  const [selection, setSelection] = useState<
    Selectable<StatusType> | undefined
  >(() => {
    return displayStatusSelectables.find((s) => currentStatusType === s.name);
  });
  const needsComment = checkNeedsComment(
    currentStatusType,
    selection?.name,
    useCanChangeStatusNoComment()
  );

  const statusUpdated = Boolean(
    selection && currentStatusType !== selection.name
  );

  const onChangeStatus = () => {
    if (selection && (!needsComment || comment)) {
      // must have made a selection to change status
      const timestamp = alarm.timestamp;
      const date = dayjs.utc(timestamp);
      const partitionKey = date.format('YYYYMM');
      const update = {
        newStatus: selection.name,
        timestamp: new Date().toISOString(),
        comment: comment?.name,
        partitionKey: partitionKey,
      };

      mutation.mutate(update);
    }
  };

  const getItems = (q: string) => {
    return getSelectItemsSelectable(q, comment, commentOptions);
  };

  const actions = (
    <>
      <Button
        variant="primary"
        onClick={onChangeStatus}
        // Only enable if status actually changes
        // and comment has been selected (only if it is mandatory)
        disabled={
          !statusUpdated ||
          (needsComment && !comment) ||
          (query !== '' && query !== comment?.name)
        }
        onSideEffect={eventTracker('submit_alarm_status_update')}
        loading={mutation.isLoading}
      >
        Submit
      </Button>
      <Button
        onClick={onCloseCb}
        onSideEffect={eventTracker('cancel_alarm_status_update')}
      >
        Cancel
      </Button>
    </>
  );

  return (
    <Modal
      open={open}
      onClose={onCloseCb}
      title="Update Status"
      footer={actions}
    >
      <div className="w-96">
        <div className="p-8">
          <h2 className="mb-1 block text-xs text-gray-700 dark:text-gray-400">
            Status
          </h2>
          {mutation.isError ? (
            <div className="py-2">
              <Alert type="error">
                Error updating alarm status. Please try again.
              </Alert>
            </div>
          ) : null}
          <Radio
            items={displayStatusSelectables}
            value={selection}
            onChange={setSelection}
            id={(item) => item.id}
            displayValue={(item) => item.name}
          />
          <div className="mt-4">
            <Select
              label="Comment"
              value={comment}
              displayValue={(item) => item?.name ?? ''}
              getItems={getItems}
              onChange={setComment}
              id={(item) => item.id}
              onChangeQuery={setQuery}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}
